import { create } from "zustand";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import toast from "react-hot-toast";
import {
    START_SETUP_ASSISTANT_SESSION,
    SEND_SETUP_ASSISTANT_MESSAGE,
    APPLY_SETUP_PROPOSAL,
    DISMISS_SETUP_PROPOSAL,
    END_SETUP_ASSISTANT_SESSION,
    GET_SETUP_CHECKLIST,
} from "../graphql/operations/setupAssistant";

export type SetupProposalStatus = "pending" | "applied" | "dismissed" | "failed";
export type SetupProposalRisk = "low" | "medium" | "destructive";

export interface SetupProposal {
    id: string;
    operationId: string;
    summary: string;
    rationale?: string | null;
    risk: SetupProposalRisk;
    status: SetupProposalStatus;
    body?: Record<string, unknown> | null;
    error?: string | null;
    appliedAt?: string | null;
    createdAt?: string | null;
}

export interface SetupAssistantMessage {
    role: "user" | "assistant";
    content: string;
    createdAt?: string | null;
    toolCalls: { name: string; operationId?: string | null; ok: boolean }[];
}

export interface SetupAssistantSession {
    id: string;
    status: "active" | "ended" | "expired";
    scopes: string[];
    expiresAt: string;
    messages: SetupAssistantMessage[];
    proposals: SetupProposal[];
}

interface SetupAssistantState {
    open: boolean;
    session: SetupAssistantSession | null;
    /**
     * The org the live session was minted for. A session carries its own
     * transcript, scopes and API key, so it is only ever valid for that one
     * workspace — switching orgs has to start a new one rather than reuse this.
     */
    sessionOrganizationId: string | null;
    starting: boolean;
    sending: boolean;
    applyingProposalId: string | null;
    error: string | null;
    /**
     * Queued while the session is still starting. Opening the drawer from a
     * hint means the user's question arrives before there is a session to send
     * it to, and dropping it would look like the click did nothing.
     */
    pendingMessage: string | null;
}

interface SetupAssistantActions {
    openAssistant: (prefill?: string) => void;
    closeAssistant: () => void;
    startSession: (organizationId: string, client: ApolloClient<NormalizedCacheObject>) => Promise<void>;
    sendMessage: (message: string, client: ApolloClient<NormalizedCacheObject>) => Promise<void>;
    applyProposal: (
        proposalId: string,
        organizationId: string,
        client: ApolloClient<NormalizedCacheObject>,
    ) => Promise<void>;
    dismissProposal: (proposalId: string, client: ApolloClient<NormalizedCacheObject>) => Promise<void>;
    endSession: (client: ApolloClient<NormalizedCacheObject>) => Promise<void>;
    consumePendingMessage: () => string | null;
    clearError: () => void;
    reset: () => void;
}

const errorMessage = (err: unknown, fallback: string) =>
    (err as any)?.graphQLErrors?.[0]?.message || (err as any)?.message || fallback;

const initialState: SetupAssistantState = {
    open: false,
    session: null,
    sessionOrganizationId: null,
    starting: false,
    sending: false,
    applyingProposalId: null,
    error: null,
    pendingMessage: null,
};

export const useSetupAssistantStore = create<SetupAssistantState & SetupAssistantActions>((set, get) => ({
    ...initialState,

    openAssistant: (prefill) => set({ open: true, pendingMessage: prefill ?? null, error: null }),
    closeAssistant: () => set({ open: false }),
    clearError: () => set({ error: null }),

    consumePendingMessage: () => {
        const { pendingMessage } = get();
        if (pendingMessage) set({ pendingMessage: null });
        return pendingMessage;
    },

    startSession: async (organizationId, client) => {
        const { session, sessionOrganizationId, starting } = get();
        if (starting) return;
        if (session && sessionOrganizationId === organizationId) return;
        // Drop a session belonging to another workspace before asking for a new
        // one, so nothing renders — or gets applied — against the wrong org.
        set({ starting: true, error: null, session: null, sessionOrganizationId: null, applyingProposalId: null });
        try {
            const { data } = await client.mutate({
                mutation: START_SETUP_ASSISTANT_SESSION,
                variables: { organizationId },
            });
            set({
                session: data.startSetupAssistantSession,
                sessionOrganizationId: organizationId,
                starting: false,
            });
        } catch (err) {
            set({ starting: false, error: errorMessage(err, "Could not start the setup assistant.") });
        }
    },

    sendMessage: async (message, client) => {
        const session = get().session;
        if (!session || !message.trim() || get().sending) return;

        // Echo the user's turn immediately; the server returns the canonical
        // transcript, which replaces this optimistic copy.
        set((state) => ({
            sending: true,
            error: null,
            session: state.session
                ? {
                      ...state.session,
                      messages: [...state.session.messages, { role: "user", content: message, toolCalls: [] }],
                  }
                : state.session,
        }));

        try {
            const { data } = await client.mutate({
                mutation: SEND_SETUP_ASSISTANT_MESSAGE,
                variables: { sessionId: session.id, message },
            });
            set({ session: data.sendSetupAssistantMessage.session, sending: false });
        } catch (err) {
            const problem = errorMessage(err, "The assistant could not answer that.");
            // Roll the optimistic turn back so a retry does not double it up.
            set((state) => ({
                sending: false,
                error: problem,
                session: state.session
                    ? { ...state.session, messages: state.session.messages.slice(0, -1) }
                    : state.session,
            }));
        }
    },

    applyProposal: async (proposalId, organizationId, client) => {
        const { session, sessionOrganizationId } = get();
        if (!session || sessionOrganizationId !== organizationId) return;
        set({ applyingProposalId: proposalId, error: null });
        try {
            const { data } = await client.mutate({
                mutation: APPLY_SETUP_PROPOSAL,
                variables: { sessionId: session.id, proposalId },
                // The change lands in the product, so the checklist and
                // whatever page is open both need to see it.
                refetchQueries: [{ query: GET_SETUP_CHECKLIST, variables: { organizationId } }],
            });
            const proposal: SetupProposal = data.applySetupProposal;
            set((state) => ({
                applyingProposalId: null,
                session: state.session ? replaceProposal(state.session, proposal) : state.session,
            }));

            if (proposal.status === "applied") toast.success(proposal.summary);
            else toast.error(proposal.error || "That change could not be applied.");
        } catch (err) {
            const problem = errorMessage(err, "That change could not be applied.");
            set({ applyingProposalId: null, error: problem });
            toast.error(problem);
        }
    },

    dismissProposal: async (proposalId, client) => {
        const session = get().session;
        if (!session) return;
        try {
            const { data } = await client.mutate({
                mutation: DISMISS_SETUP_PROPOSAL,
                variables: { sessionId: session.id, proposalId },
            });
            set((state) => ({
                session: state.session ? replaceProposal(state.session, data.dismissSetupProposal) : state.session,
            }));
        } catch (err) {
            set({ error: errorMessage(err, "Could not dismiss that change.") });
        }
    },

    endSession: async (client) => {
        const session = get().session;
        set({ ...initialState });
        if (!session) return;
        // Best-effort: the session key expires on its own, so a failed revoke
        // is not worth surfacing to the user.
        await client
            .mutate({ mutation: END_SETUP_ASSISTANT_SESSION, variables: { sessionId: session.id } })
            .catch(() => undefined);
    },

    /**
     * Drop everything the signed-in person put here. The store outlives a
     * logout — nothing remounts the app — so without this the next person to
     * sign in on the same tab would open the drawer onto the previous user's
     * transcript and proposal history. The server session is left to its TTL:
     * the token needed to revoke it is already gone by the time this runs.
     */
    reset: () => set({ ...initialState }),
}));

function replaceProposal(session: SetupAssistantSession, proposal: SetupProposal): SetupAssistantSession {
    return {
        ...session,
        proposals: session.proposals.map((p) => (p.id === proposal.id ? proposal : p)),
    };
}
